import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useHouseholdId } from './useHouseholdId'

export type SuggestedListItem = {
  itemId: string
  name:   string
  reason: 'out' | 'low' | 'expiring'
}

export function useSuggestedList(): SuggestedListItem[] {
  const householdId = useHouseholdId()
  const [items, setItems] = useState<SuggestedListItem[]>([])

  useEffect(() => {
    if (!householdId) return
    let cancelled = false

    ;(async () => {
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, status_override, last_confirmed_at, avg_restock_cycle_days, confirmation_count')
        .eq('household_id', householdId)
        .eq('is_active', true)

      if (cancelled) return

      const today   = new Date()
      const result: SuggestedListItem[] = []

      for (const item of data ?? []) {
        // Explicitly marked out
        if (item.status_override === 'out') {
          result.push({ itemId: item.id, name: item.name, reason: 'out' })
          continue
        }

        // Pattern-engine approximation: item has enough confirmations to trust the cycle
        if (
          item.avg_restock_cycle_days &&
          item.last_confirmed_at &&
          item.confirmation_count >= 3
        ) {
          const daysSince = (today.getTime() - new Date(item.last_confirmed_at).getTime()) / 86_400_000
          const depletion = daysSince / item.avg_restock_cycle_days
          if (depletion >= 1.0) {
            result.push({ itemId: item.id, name: item.name, reason: 'out' })
          } else if (depletion >= 0.8) {
            result.push({ itemId: item.id, name: item.name, reason: 'low' })
          }
        }
      }

      setItems(result.slice(0, 12))
    })()

    return () => { cancelled = true }
  }, [householdId])

  return items
}
